/**
 * MobileBlockFullscreen Component
 * 
 * Fullscreen overlay for viewing expanded block content.
 * Takes over the entire screen for immersive content viewing.
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import './mobile.css';

export interface MobileBlockFullscreenProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    type: 'note' | 'pdf' | 'youtube' | 'quiz' | 'slide-deck' | 'explainer';
    children: React.ReactNode;
}

const MobileBlockFullscreen: React.FC<MobileBlockFullscreenProps> = ({
    isOpen,
    onClose,
    title,
    type,
    children,
}) => {
    // Prevent body scroll when fullscreen is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Immersive types get minimal chrome
    const isImmersive = type === 'pdf' || type === 'slide-deck' || type === 'explainer';

    return (
        <div className="mobile-fullscreen-overlay">
            {/* Thin header */}
            <div className={`mobile-fullscreen-header ${isImmersive ? 'immersive' : ''}`}>
                <button
                    onClick={onClose}
                    className="mobile-fullscreen-close"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>
                {!isImmersive && (
                    <h2 className="mobile-fullscreen-title">{title}</h2>
                )}
                {!isImmersive && (
                    <div className="w-10" />
                )}
            </div>

            {/* Content area - full remaining space */}
            <div className="mobile-fullscreen-content">
                {children}
            </div>
        </div>
    );
};

export default MobileBlockFullscreen;
